import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { Table } from 'src/components/base/Table';
import { LargeContainer, SimpleHeader } from 'src/components/base/Elements';
import { SubmitAction } from 'src/components/base/SubmitAction';
import { useModalDialog } from 'src/components/base/ModalDialog';
import { useFetchLocation, useUpdateEffect } from 'src/utils/hooks';
import { useWorkspace } from 'src/model/workspaces';
import { useFetchCatches } from 'src/model/catches';
import { CatchesFilterRow } from 'src/components/catches/CatchesFilterRow';
import { CatchIndicators } from 'src/components/catches/CatchIndicators';
import { CatchDialog } from 'src/components/catches/CatchDialog';
import { buildCatchesFilter, parseCatchesFilter, DEFAULT_CATCH_FILTERS, hasActiveCatchesFilters } from 'src/utils/catches';
import { formatFromISO } from 'src/utils/datefns';

import classes from './Catches.module.scss';

const PAGE_SIZE = 50;
const AGES = Object.fromEntries(config.catch.ages.map(({ type, title }) => [type.toLowerCase(), title]));
const GENDERS = Object.fromEntries(config.catch.genders.map(({ type, title }) => [type.toLowerCase(), title]));

export const Catches = () => {
  const workspace = useWorkspace();
  const animals = useMemo(() => Object.fromEntries((workspace.animals ?? []).map(({ type, title }) => [type.toLowerCase(), title])), [workspace.animals]);

  const catchDialogModal = useModalDialog();
  const { pageIndex, sortBy, filter: filterFromUrl, setFetchLocation } = useFetchLocation();

  const [filters, setFilters] = useState(() => parseCatchesFilter(filterFromUrl));
  const syncFromUrlRef = useRef(false);
  const sortByRef = useRef(sortBy);
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);

  useEffect(() => {
    syncFromUrlRef.current = true;
    setFilters(parseCatchesFilter(filterFromUrl));
  }, [filterFromUrl]);

  useUpdateEffect(() => {
    if (syncFromUrlRef.current) {
      syncFromUrlRef.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      const apiFilter = buildCatchesFilter(filters);
      setFetchLocation({ pageIndex: 0, sortBy: sortByRef.current, filter: apiFilter || undefined });
    }, 300);

    return () => clearTimeout(timeout);
  }, [filters]);

  const { data, mutate } = useFetchCatches({ pageIndex, sortBy, pageSize: PAGE_SIZE, filter: filterFromUrl });
  const hasActiveFilters = useMemo(() => hasActiveCatchesFilters(filters), [filters]);

  function handleFetchData({ pageIndex, sortBy }) {
    setFetchLocation({ pageIndex, sortBy, filter: filterFromUrl });
  }

  const handleEditItem = useCallback((item) => {
    catchDialogModal.show({ catchItem: item, mutate });
  }, [catchDialogModal, mutate]);

  const columns = useMemo(
    () => [
      {
        Header: __('Trap'),
        accessor: 'trap_name',
        className: 'fw-bold',
        style: { width: '15%' },
      },
      {
        Header: __('Type'),
        accessor: 'trap_type',
        style: { width: '10%' },
      },
      {
        Header: __('Date/Time'),
        accessor: 'time',
        Cell: ({ value }) => formatFromISO(value, 'Pp'),
        style: { width: '10%' },
      },
      {
        Header: __('Animal'),
        accessor: 'animal',
        Cell: ({ value, row: { original } }) => animals[value] ?? original?.other_animal ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Age'),
        accessor: 'age',
        Cell: ({ value }) => AGES[value] ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Sex'),
        accessor: 'sex',
        Cell: ({ value }) => GENDERS[value] ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Bait'),
        accessor: 'bait',
        style: { width: '10%' },
      },
      {
        Header: __('Map/Photos'),
        id: 'map_photos',
        disableSortBy: true,
        Cell: ({ row: { original } }) => <CatchIndicators item={original} />,
        style: { width: '8%' },
      },
      {
        Header: __('Comment'),
        accessor: 'notes',
        Cell: ({ row: { original } }) => <LastCell item={original} onEdit={handleEditItem} />,
        style: { width: '17%', position: 'relative' },
      },
    ],
    [animals, handleEditItem]
  )

  function handleClearFilters() {
    syncFromUrlRef.current = true;
    setFilters(DEFAULT_CATCH_FILTERS);
    setFetchLocation({ pageIndex: 0, sortBy, filter: undefined });
  }

  return (
    <LargeContainer className={classes.root}>
      <div className="mt-4 mb-5 d-flex justify-content-between align-items-baseline">
        <SimpleHeader>{__('Catches')}</SimpleHeader>
        <SubmitAction action={`${config.api.serverUrl}/exportcatches`}>{__('Export All to CSV')}</SubmitAction>
      </div>

      <div className={classes.filterBar}>
        <div className={classes.filterCount}>
          <span className={classes.filterCountText}>
            {__('Showing')}
            <span>{data?.items?.length ?? 0}</span>
            {__('of')}
            <span>{data?.total ?? 0}</span>
            {__('catches')}
          </span>
          <Button
            className={classes.clearFiltersButton}
            variant="secondary"
            size="sm"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            {__('Clear filters')}
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={data.items}
        pageIndex={pageIndex}
        pageCount={data.pageCount}
        pageSize={PAGE_SIZE}
        sortBy={sortBy}
        fetchData={handleFetchData}
        renderFilterRow={() => (
          <CatchesFilterRow
            filters={filters}
            onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            typeOptions={workspace.trap_types ?? []}
            animalOptions={workspace.animals ?? []}
            ageOptions={config.catch.ages}
            sexOptions={config.catch.genders}
          />
        )}
        filterRowClassName={classes.filterRow}
      />

      <CatchDialog {...catchDialogModal.register()} />
    </LargeContainer>
  )
}

const LastCell = ({ item, onEdit }) => (
  <>
    <span>{item.notes}</span>
    <Button className={classes.editButton} variant="primary" onClick={() => onEdit(item)}>{__('Edit')}</Button>
  </>
);
